/**
 * Transaction reversal view (task 21.2): reverse an eligible transaction
 * (R6.1).
 *
 * The page lists the selected account's recent transactions and offers a
 * Reverse action on those the customer can still reverse — `posted` and within
 * the 3-day window (R6.2), mirrored client-side for display via
 * {@link isReversibleNow}. Posted rows older than 3 days stay visible but
 * greyed with "No longer reversible" so the rule is legible; reversed/
 * reversing rows are omitted. Each row reads like a bank statement: the primary
 * line ({@link transactionPrimaryLine}, e.g. "Payment to Byron Snyman") with the
 * customer's payment reference — when present ({@link transactionReference}) —
 * shown as a smaller, muted second line beneath it.
 * Clicking Reverse opens an inline {@link ConfirmAction} panel ("Reverse R55.00 ·
 * Rent March · 25 Sept?", naming the inherited reference in the body text) and
 * only Confirm submits `POST /transactions/{id}/reverse` — the same call and
 * payload as before. A success shows a note and reloads both the list and the
 * accounts.
 *
 * An "Advanced: reverse by transaction ID" `<details>` (collapsed by default)
 * keeps the original id input + submit for customers acting on a statement
 * reference. Eligibility failures surface as the standard envelope —
 * out-of-window (`REVERSAL_OUT_OF_WINDOW`, R6.2), already reversed
 * (`ALREADY_REVERSED`, R6.3), or not owned (`NOT_AUTHORIZED`, R6.4) — rendered
 * inline. The request carries an idempotency key so a retry is safe.
 */

import { useId, useState, type FormEvent } from 'react';

import {
  accountsApi,
  reversalApi,
  type Transaction,
  type TransactionPage,
} from '../api/index.js';
import { ConfirmAction } from '../ui/ConfirmAction.js';
import { ErrorNotice } from '../ui/ErrorNotice.js';
import {
  REVERSAL_WINDOW_DAYS,
  categoryLabel,
  formatMoney,
  formatRelativeDate,
  formatShortDate,
  isReversibleNow,
  reversalDeadline,
  transactionPrimaryLine,
  transactionReference,
  transactionTitle,
} from '../ui/format.js';
import { useAction, useAsyncData } from '../ui/useAsync.js';

/** Whether the formatted money string represents a credit (non-negative). */
function isCredit(amount: string, currency: string): boolean {
  return !formatMoney(amount, currency).trim().startsWith('-');
}

/** Last four characters of an account id, for a masked reference display. */
function maskedRef(accountId: string): string {
  return `•••• ${accountId.slice(-4)}`;
}

/** Whether the transaction carries a non-blank customer payment reference. */
function hasReference(tx: Transaction): boolean {
  return typeof tx.reference === 'string' && tx.reference.trim().length > 0;
}

/** Reverse-a-transaction panel: recent reversible list + advanced id fallback. */
export function ReversalView(): JSX.Element {
  const accounts = useAsyncData(() => accountsApi.listAccounts(), []);
  const [accountId, setAccountId] = useState('');
  const [pendingTx, setPendingTx] = useState<Transaction | undefined>(undefined);
  const [transactionId, setTransactionId] = useState('');
  const [confirmation, setConfirmation] = useState<string | undefined>(undefined);
  const accountSelectId = useId();
  const inputId = useId();

  const accountList = accounts.data?.accounts ?? [];
  const resolvedAccountId = accountId || (accountList[0]?.accountId ?? '');
  const selectedAccount = accountList.find((a) => a.accountId === resolvedAccountId);

  const transactions = useAsyncData<TransactionPage>(
    () =>
      resolvedAccountId === ''
        ? Promise.resolve({ transactions: [], nextCursor: null })
        : accountsApi.getTransactions(resolvedAccountId),
    [resolvedAccountId],
  );

  const reverse = useAction(reversalApi.reverseTransaction);

  /** Posted rows only (reversed/reversing omitted); newest first as served. */
  const postedRows = (transactions.data?.transactions ?? []).filter(
    (tx) => tx.status.toLowerCase() === 'posted',
  );

  async function commitReversal(id: string, note: string): Promise<boolean> {
    setConfirmation(undefined);
    const result = await reverse.run(id);
    if (result !== undefined) {
      setConfirmation(note);
      transactions.reload();
      accounts.reload();
      return true;
    }
    return false;
  }

  /** Confirm-panel path: reverse the selected row. */
  async function onConfirmRow(tx: Transaction): Promise<void> {
    const ok = await commitReversal(
      tx.transactionId,
      `${formatMoney(tx.amount, tx.currency)} · ${transactionTitle(tx)} reversed.`,
    );
    if (ok) {
      setPendingTx(undefined);
    }
  }

  /** Advanced path: reverse by a typed transaction id. */
  async function onSubmitById(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const id = transactionId.trim();
    if (id.length === 0) {
      return;
    }
    const ok = await commitReversal(id, 'Reversal submitted.');
    if (ok) {
      setTransactionId('');
    }
  }

  return (
    <section aria-labelledby="reversal-heading">
      <h2 id="reversal-heading" className="esb-section-title">Reverse a transaction</h2>

      <ErrorNotice error={accounts.error} />
      <ErrorNotice error={transactions.error} />
      <ErrorNotice error={reverse.error} />
      {confirmation !== undefined ? <p role="status" className="esb-status">{confirmation}</p> : null}

      <section aria-labelledby="reversible-heading" className="esb-card">
        <div className="esb-feed-card__head">
          <h3 id="reversible-heading" className="esb-subhead">Recent transactions you can reverse</h3>
          {selectedAccount !== undefined ? (
            <span className="esb-feed-card__acct">
              {selectedAccount.accountType} · {maskedRef(selectedAccount.accountId)}
            </span>
          ) : null}
        </div>

        <p className="esb-field__hint esb-rule">
          Transactions can be reversed within {REVERSAL_WINDOW_DAYS} days of the transaction date.
        </p>

        {accountList.length > 0 ? (
          <div className="esb-field">
            <label htmlFor={accountSelectId} className="esb-field__label">Account</label>
            <select
              id={accountSelectId}
              className="esb-select"
              value={resolvedAccountId}
              onChange={(e) => {
                setAccountId(e.target.value);
                setPendingTx(undefined);
              }}
            >
              {accountList.map((a) => (
                <option key={a.accountId} value={a.accountId}>
                  {a.accountType} · {maskedRef(a.accountId)} — {formatMoney(a.balance, a.currency)}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div aria-live="polite">
          {accounts.loading || transactions.loading ? <p className="esb-loading">Loading transactions…</p> : null}
        </div>

        {!accounts.loading && accounts.error === undefined && accountList.length === 0 ? (
          <p className="esb-empty">You have no accounts.</p>
        ) : null}

        {!transactions.loading && transactions.data !== undefined && postedRows.length === 0 && accountList.length > 0 ? (
          <p className="esb-empty">No posted transactions on this account.</p>
        ) : null}

        {postedRows.length > 0 ? (
          <div className="esb-feed-scroll">
            <ul
              className="esb-feed"
              role="list"
              aria-label={`Recent transactions for account ${resolvedAccountId}`}
            >
              {postedRows.map((tx) => {
                const reversible = isReversibleNow(tx);
                const deadline = reversalDeadline(tx.transactionDate);
                const credit = isCredit(tx.amount, tx.currency);
                const isPending = pendingTx?.transactionId === tx.transactionId;
                return (
                  <li
                    key={tx.transactionId}
                    className={`esb-feed__row esb-feed__row--plain${reversible ? '' : ' esb-feed__row--expired'}`}
                    role="listitem"
                  >
                    <span className="esb-feed__body">
                      <span className="esb-feed__desc">{transactionPrimaryLine(tx)}</span>
                      {transactionReference(tx) !== null ? (
                        <span className="esb-feed__ref">{transactionReference(tx)}</span>
                      ) : null}
                      <span className="esb-feed__meta">
                        <span className="esb-feed__date">{formatRelativeDate(tx.transactionDate)}</span>
                        <span className="esb-chip">{categoryLabel(tx.category)}</span>
                      </span>
                      <span className="esb-feed__until">
                        {reversible && deadline !== undefined
                          ? `Reversible until ${formatShortDate(deadline.toISOString())}`
                          : 'No longer reversible'}
                      </span>
                    </span>
                    <span className="esb-feed__right">
                      <span className={credit ? 'esb-amount--pos' : 'esb-amount--neg'}>
                        {formatMoney(tx.amount, tx.currency)}
                      </span>
                    </span>
                    <span className="esb-feed__action">
                      {reversible && !isPending ? (
                        <button
                          type="button"
                          className="esb-btn esb-btn--ghost esb-btn--sm"
                          onClick={() => {
                            setConfirmation(undefined);
                            setPendingTx(tx);
                          }}
                          disabled={reverse.pending}
                        >
                          Reverse
                        </button>
                      ) : null}
                    </span>
                    {isPending ? (
                      <div className="esb-feed__confirm">
                        <ConfirmAction
                          title={`Reverse ${formatMoney(tx.amount, tx.currency)} · ${transactionTitle(tx)} · ${formatShortDate(tx.transactionDate)}?`}
                          confirmLabel="Confirm reversal"
                          pendingLabel="Reversing…"
                          pending={reverse.pending}
                          tone="danger"
                          onConfirm={() => void onConfirmRow(tx)}
                          onCancel={() => setPendingTx(undefined)}
                        >
                          <p className="esb-confirm__text">
                            The amount is returned to {selectedAccount?.accountType ?? 'the account'} and a
                            reversing transaction is recorded
                            {hasReference(tx) ? ` with the reference "${tx.reference}"` : ''}. This cannot be undone.
                          </p>
                        </ConfirmAction>
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </section>

      <details className="esb-card esb-details">
        <summary className="esb-details__summary">Advanced: reverse by transaction ID</summary>
        <form onSubmit={(e) => void onSubmitById(e)} aria-labelledby="reversal-heading" noValidate className="esb-details__body">
          <div className="esb-field">
            <label htmlFor={inputId} className="esb-field__label">Transaction ID</label>
            <input
              id={inputId}
              className="esb-field__input"
              value={transactionId}
              onChange={(e) => setTransactionId(e.target.value)}
              required
            />
          </div>
          <button
            type="submit"
            className="esb-btn esb-btn--primary"
            disabled={reverse.pending || transactionId.trim().length === 0}
          >
            {reverse.pending ? 'Reversing…' : 'Reverse'}
          </button>
        </form>
      </details>
    </section>
  );
}
